import { useEffect, useState } from 'react'
import { useMutation } from '@apollo/client/react'
import { Button, Divider, Group, Stack, Text, TextInput, PasswordInput, Alert, Anchor } from '@mantine/core'
import { emailIsValid } from '../util/validation'
import CreateAccountWithEmail from '../graphql/queries/CreateAccountWithEmail'
import CurrentUser from '../graphql/queries/CurrentUser'
import { useModalStack } from '../ui/ModalStack'
import { Actions } from '../AppReducer'
import { useAppDispatch } from '../AppContext'
import GoogleLoginButton from './GoogleLoginButton'
import AppleLoginButton from './AppleLoginButton'

export default function EmailCreateAccount({ onClose }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const { pushModal } = useModalStack()
  const dispatch = useAppDispatch()

  const [createAccountMutation, { loading, data, error, reset }] = useMutation(CreateAccountWithEmail, {
    fetchPolicy: 'no-cache',
    update: addUserToCache
  })

  function addUserToCache(cache, { data }) {
    cache.writeQuery({
      query: CurrentUser,
      data: data.createUserForEmail
    })
  }

  useEffect(() => {
    if (data?.createUserForEmail?.accessToken) {
      const { createUserForEmail: { accessToken, refreshToken, user } } = data
      localStorage.setItem('at', accessToken)
      if (refreshToken) localStorage.setItem('rt', refreshToken)
      if (user?.email) localStorage.setItem('email', user.email)
      reset()
      dispatch(Actions.logIn())
      onClose?.()
      // New accounts are unverified — prompt them to check their email
      const roles = user?.roles ?? []
      if (!roles.includes('verified') && !roles.includes('guest')) {
        pushModal('unverifiedAccount', { email: user?.email })
      }
    }
  }, [data, dispatch, onClose, pushModal, reset])

  useEffect(() => {
    if (error) {
      console.error('Create account error:', error)

      const isUserExistsError =
        error.graphQLErrors?.find(gqlErr =>
          gqlErr.extensions?.code === 'USER_EXISTS' ||
          gqlErr.message?.toLowerCase().includes('user exists')
        ) ||
        error.message?.toLowerCase().includes('user exists')

      const isPasswordError =
        error.graphQLErrors?.find(gqlErr => gqlErr.extensions?.code === 'UNACCEPTABLE_PASSWORD')

      if (isUserExistsError) {
        setErrorMessage('There is already an account for that email address.')
      } else if (isPasswordError) {
        setErrorMessage('The password you provided is not acceptable. Please try again.')
      } else {
        setErrorMessage('There was an error creating your account. Please try again.')
      }
      reset()
    }
  }, [error, reset])

  function emailUpdated(e) {
    setErrorMessage('')
    setEmail(e.target.value)
  }

  function passwordUpdated(e) {
    setErrorMessage('')
    setPassword(e.target.value)
  }

  function confirmUpdated(e) {
    setErrorMessage('')
    setConfirm(e.target.value)
  }

  function validateEmail() {
    if (!emailIsValid(email)) return 'Invalid email address.'
    return null
  }

  function validatePassword() {
    if (password.length < 8) return 'Your password must be at least 8 characters long.'
    if (password !== confirm) return 'Your password and the confirmation don\'t match.'
    return null
  }

  async function createAccount() {
    const emailProblem = validateEmail()
    if (emailProblem) { setErrorMessage(emailProblem); return }
    const passwordProblem = validatePassword()
    if (passwordProblem) { setErrorMessage(passwordProblem); return }
    try {
      await createAccountMutation({
        variables: {
          email,
          password
        }
      })
    } catch (err) {
      // Error handling is in useEffect
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !loading) {
      createAccount()
    }
  }

  function switchToLogin() {
    onClose?.()
    pushModal('emailLogin')
  }

  return (
    <Stack gap="sm" style={{ marginTop: '1em' }}>
      <Text size="xs" c="dimmed" style={{ textAlign: 'center' }}>
        By creating an account, you agree to our{' '}
        <Anchor size="xs" href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Anchor>
        {' '}and{' '}
        <Anchor size="xs" href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Anchor>.
      </Text>
      <GoogleLoginButton onClose={onClose} />
      <AppleLoginButton onClose={onClose} />
      <Divider label="or continue with email" labelPosition="center" my="xs" />
      <TextInput
        label="Email"
        placeholder="you@example.com"
        value={email}
        onChange={emailUpdated}
        type="email"
        onKeyDown={handleKeyDown}
      />
      <PasswordInput
        label="Password"
        placeholder="At least 8 characters"
        value={password}
        onChange={passwordUpdated}
        onKeyDown={handleKeyDown}
      />
      <PasswordInput
        label="Confirm Password"
        placeholder="Re-enter your password"
        value={confirm}
        onChange={confirmUpdated}
        onKeyDown={handleKeyDown}
      />
      { errorMessage ? (
        <Alert color="red" variant="light">
          <Text size="sm">{errorMessage}</Text>
        </Alert>
      ) : null }
      <Group justify="space-between" mt="xs">
        <Button variant="subtle" onClick={switchToLogin}>
          Already have an account?
        </Button>
        <Group>
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={loading} onClick={createAccount}>
            Create Account
          </Button>
        </Group>
      </Group>
    </Stack>
  )
}
